import MediaflowService from "@/services/MediaflowService";
import router from "@/router";
import _ from "lodash";

const normalizeMedia = (media) => {
  if (!media || typeof media !== "object") return;
  if (media.volume === undefined || media.volume === null) media.volume = 50;
  if (typeof media.show === "undefined") media.show = false;
  if (typeof media.muted === "undefined") media.muted = false;
  if (typeof media.audioOffset === "undefined") media.audioOffset = 0;
};

/** A production-level graphic as the new engine returns it; older servers send none. */
const normalizeOverlay = (overlay, index) => {
  const o = { ...overlay };
  if (!o.row) o.row = "Graphics";
  if (typeof o.order !== "number") o.order = index;
  o.live = !!o.live;
  // Server default (castr #3083): graphics fade in and out over 300 ms.
  if (!o.transition || typeof o.transition !== "object") o.transition = { type: "Fade", duration: 300 };
  if (o.media && typeof o.media === "object") {
    // Overlay media is always drawn when the overlay is on; `show` is the overlay's `live`.
    o.media = { ...o.media, show: true };
    if (!o.media.id) o.media.id = o.media._id || `overlay-${o._id}`;
    normalizeMedia(o.media);
  }
  return o;
};

const normalizeMediaflow = (mediaflow) => {
  const normalized = _.cloneDeep(mediaflow || {});
  if (Array.isArray(normalized.flows)) {
    normalized.flows.forEach((flow) => {
      // `kind` arrives only from the new engine; everything older is a layout.
      if (flow.kind !== "break") flow.kind = "layout";
      if (flow.kind === "break") {
        flow.break = {
          durationSec: Number(flow.break?.durationSec) || 30,
          returnFlowId: flow.break?.returnFlowId ?? null,
          overlays: flow.break?.overlays === "hide" ? "hide" : "keep",
        };
      }
      if (!Array.isArray(flow.media)) return;
      flow.media.forEach(normalizeMedia);
    });
  }
  normalized.overlays = Array.isArray(normalized.overlays)
    ? normalized.overlays.map(normalizeOverlay).sort((a, b) => a.order - b.order)
    : [];
  normalized.capabilities = {
    overlays: !!normalized.capabilities?.overlays,
    cueBreaks: !!normalized.capabilities?.cueBreaks,
  };
  return normalized;
};

const defaultState = () => ({
  mediaflows: [],
  mediaflow: {},
  currentMediaflowId: "",
  mediaflowLoaded: false,
  teamPermissionError: false,
  isLoading: false,
  isLoaded: false,
});

const state = defaultState();

const mutations = {
  SET_LOADING(state, isLoading) {
    state.isLoading = isLoading;
  },
  GET_MEDIAFLOWS(state, mediaflows) {
    state.mediaflows = mediaflows;
    state.isLoaded = true;
    state.isLoading = false;
  },
  GET_MEDIAFLOW(state, mediaflow) {
    state.mediaflow = mediaflow;
    state.mediaflowLoaded = true;
  },
  SET_CURRENT_MEDIAFLOW(state, id) {
    state.currentMediaflowId = id;
  },
  SET_MEDIAFLOW_SOURCE_STREAM(state, { index, stream }) {
      state.mediaflow.streams[index] = stream
  },
  REMOVE_MEDIAFLOW(state, id) {
    const objIndex = state.mediaflows.findIndex(obj => { 
      return obj._id === id
    })
    if (objIndex >= 0) {
      state.mediaflows.splice(objIndex, 1)
    }
  },
  TOGGLE_MEDIAFLOW(state, id) {
    const objIndex = state.mediaflows.findIndex(obj => { 
      return obj._id === id
    })

    if (objIndex >= 0) {
      Object.assign(state.mediaflows[objIndex], { enabled: !state.mediaflows[objIndex].enabled })
      state.mediaflow.enabled = !state.mediaflow.enabled
    }
  },
  SET_MEDIAFLOW_NAME(state, { id, name }) {
    const objIndex = state.mediaflows.findIndex(obj => { 
      return obj._id === id
    })

    if (objIndex >= 0) {
      Object.assign(state.mediaflows[objIndex], { name })
      state.mediaflow.name = name
    }
  },
  SET_MEDIAFLOW_DESTINATION(state, { id, destination }) {
    const objIndex = state.mediaflows.findIndex(obj => { 
      return obj._id === id
    })

    if (objIndex >= 0) {
      Object.assign(state.mediaflows[objIndex], { destination })
      state.mediaflow.destination = destination
    }
  },
  SET_MEDIAFLOW_RESOLUTION_PRESET(state, { id, resolutionPreset }) {
    const objIndex = state.mediaflows.findIndex(obj => { 
      return obj._id === id
    })

    if (objIndex >= 0) {
      Object.assign(state.mediaflows[objIndex], { resolutionPreset })
      state.mediaflow.resolutionPreset = resolutionPreset
    }
  },
  SET_MEDIAFLOW_SCREEN_RESOLUTION(state, { id, screenResolution }) {
    const objIndex = state.mediaflows.findIndex(obj => {
      return obj._id === id
    })

    if (objIndex >= 0) {
      Object.assign(state.mediaflows[objIndex], { screenResolution })
      state.mediaflow.screenResolution = screenResolution
    }
  },
  SET_MEDIAFLOW_BITRATE(state, { id, bitrate }) {
    const objIndex = state.mediaflows.findIndex(obj => { 
      return obj._id === id
    })

    if (objIndex >= 0) {
      Object.assign(state.mediaflows[objIndex], { bitrate })
      state.mediaflow.bitrate = bitrate
    }
  },
  SET_MEDIAFLOW_FPS(state, { id, fps }) {
    const objIndex = state.mediaflows.findIndex(obj => { 
      return obj._id === id
    })

    if (objIndex >= 0) {
      Object.assign(state.mediaflows[objIndex], { fps })
      state.mediaflow.fps = fps
    }
  },
  SET_MEDIAFLOW_TEAM_PERMISSION_ERROR(state, { perm }) {
    state.teamPermissionError = perm;
  },
  ADD_FLOW(state, { mediaflowId, flow }) {
    if (state.mediaflow._id === mediaflowId) {
      if (!state.mediaflow.flows) {
        state.mediaflow.flows = [];
      }
      state.mediaflow.flows.push(flow);
    }
    const objIndex = state.mediaflows.findIndex(obj => obj._id === mediaflowId);
    if (objIndex >= 0) {
      if (!state.mediaflows[objIndex].flows) {
        state.mediaflows[objIndex].flows = [];
      }
      state.mediaflows[objIndex].flows.push(flow);
    }
  },
  ADD_FLOW_MEDIA(state, { mediaflowId, flowId, media }) {
    if (state.mediaflow._id === mediaflowId) {
      const flowIndex = state.mediaflow.flows?.findIndex(flow => flow._id === flowId);
      if (flowIndex >= 0) {
        if (!state.mediaflow.flows[flowIndex].media) {
          state.mediaflow.flows[flowIndex].media = [];
        }
        state.mediaflow.flows[flowIndex].media.push(media);
      }
    }
  },
  UPDATE_FLOW_MEDIA(state, { mediaflowId, flowId, mediaId, media }) {
    if (state.mediaflow._id === mediaflowId) {
      const flow = state.mediaflow.flows?.find(f => f._id === flowId);
      if (flow && flow.media) {
        const mediaIndex = flow.media.findIndex(m => m.id === mediaId);
        if (mediaIndex >= 0) {
          Object.assign(flow.media[mediaIndex], media);
        }
      }
    }
    
    const mf = state.mediaflows.find(mf => mf._id === mediaflowId);
    if (mf) {
      const flow = mf.flows?.find(f => f._id === flowId);
      if (flow && flow.media) {
        const mediaIndex = flow.media.findIndex(m => m.id === mediaId);
        if (mediaIndex >= 0) {
          Object.assign(flow.media[mediaIndex], media);
        }
      }
    }
  },
  UPDATE_FLOW_MEDIA_VOLUME(state, { mediaflowId, flowId, mediaId, volume }) {
    if (state.mediaflow._id === mediaflowId) {
      const flow = state.mediaflow.flows?.find(f => f._id === flowId);
      if (flow && flow.media) {
        const media = flow.media.find(m => m.id === mediaId);
        if (media) {
          media.volume = volume;
        }
      }
    }
    
    const mf = state.mediaflows.find(mf => mf._id === mediaflowId);
    if (mf) {
      const flow = mf.flows?.find(f => f._id === flowId);
      if (flow && flow.media) {
        const media = flow.media.find(m => m.id === mediaId);
        if (media) {
          media.volume = volume;
        }
      }
    }
  },
  UPDATE_FLOW_MEDIA_MUTED(state, { mediaflowId, flowId, mediaId, muted }) {
    if (state.mediaflow._id === mediaflowId) {
      const flow = state.mediaflow.flows?.find(f => f._id === flowId);
      if (flow && flow.media) {
        const media = flow.media.find(m => m.id === mediaId);
        if (media) {
          media.muted = muted;
        }
      }
    }
    
    const mf = state.mediaflows.find(mf => mf._id === mediaflowId);
    if (mf) {
      const flow = mf.flows?.find(f => f._id === flowId);
      if (flow && flow.media) {
        const media = flow.media.find(m => m.id === mediaId);
        if (media) {
          media.muted = muted;
        }
      }
    }
  },
  UPDATE_FLOW_MEDIA_AUDIO_OFFSET(state, { mediaflowId, flowId, mediaId, audioOffset }) {
    if (state.mediaflow._id === mediaflowId) {
      const flow = state.mediaflow.flows?.find(f => f._id === flowId);
      if (flow && flow.media) {
        const media = flow.media.find(m => m.id === mediaId);
        if (media) {
          media.audioOffset = audioOffset;
        }
      }
    }
    
    const mf = state.mediaflows.find(mf => mf._id === mediaflowId);
    if (mf) {
      const flow = mf.flows?.find(f => f._id === flowId);
      if (flow && flow.media) {
        const media = flow.media.find(m => m.id === mediaId);
        if (media) {
          media.audioOffset = audioOffset;
        }
      }
    }
  },
  REORDER_FLOW_MEDIA(state, { mediaflowId, flowId, fromIndex, toIndex }) {
    if (state.mediaflow._id === mediaflowId) {
      const flow = state.mediaflow.flows?.find(f => f._id === flowId);
      // The list cache can hold fewer items than the detail: never insert a hole.
      if (flow && flow.media && fromIndex >= 0 && fromIndex < flow.media.length) {
        const mediaItem = flow.media.splice(fromIndex, 1)[0];
        flow.media.splice(toIndex, 0, mediaItem);
      }
    }
    
    const mf = state.mediaflows.find(mf => mf._id === mediaflowId);
    if (mf) {
      const flow = mf.flows?.find(f => f._id === flowId);
      // The list cache can hold fewer items than the detail: never insert a hole.
      if (flow && flow.media && fromIndex >= 0 && fromIndex < flow.media.length) {
        const mediaItem = flow.media.splice(fromIndex, 1)[0];
        flow.media.splice(toIndex, 0, mediaItem);
      }
    }
  },
  CLEAR_FLOW_MEDIA(state, { mediaflowId, flowId }) {
    if (state.mediaflow._id === mediaflowId) {
      const flowIndex = state.mediaflow.flows?.findIndex(flow => flow._id === flowId);
      if (flowIndex >= 0) {
        state.mediaflow.flows[flowIndex].media = [];
      }
    }
    const objIndex = state.mediaflows.findIndex(obj => obj._id === mediaflowId);
    if (objIndex >= 0) {
      const flowIndex = state.mediaflows[objIndex].flows?.findIndex(flow => flow._id === flowId);
      if (flowIndex >= 0) {
        state.mediaflows[objIndex].flows[flowIndex].media = [];
      }
    }
  },
  TOGGLE_FLOW_MEDIA_SHOW(state, { mediaflowId, flowId, mediaId }) {
    if (state.mediaflow._id === mediaflowId) {
      const flow = state.mediaflow.flows?.find(f => f._id === flowId);
      if (flow && flow.media) {
        const media = flow.media.find(m => m._id === mediaId);
        if (media) media.show = !media.show;
      }
    }
    const mf = state.mediaflows.find(mf => mf._id === mediaflowId);
    if (mf) {
      const flow = mf.flows?.find(f => f._id === flowId);
      if (flow && flow.media) {
        const media = flow.media.find(m => m._id === mediaId);
        if (media) media.show = !media.show;
      }
    }
  },
  DELETE_FLOW_MEDIA(state, { mediaflowId, flowId, mediaId }) {
    if (state.mediaflow._id === mediaflowId) {
      const flow = state.mediaflow.flows?.find(f => f._id === flowId);
      if (flow && flow.media) {
        const idx = flow.media.findIndex(m => m.id === mediaId);
        if (idx !== -1) flow.media.splice(idx, 1);
      }
    }
    const mf = state.mediaflows.find(mf => mf._id === mediaflowId);
    if (mf) {
      const flow = mf.flows?.find(f => f._id === flowId);
      if (flow && flow.media) {
        const idx = flow.media.findIndex(m => m.id === mediaId);
        if (idx !== -1) flow.media.splice(idx, 1);
      }
    }
  },
  REMOVE_FLOW(state, { mediaflowId, flowId }) {
    if (state.mediaflow._id === mediaflowId) {
      const flowIndex = state.mediaflow.flows?.findIndex(flow => flow._id === flowId);
      if (flowIndex >= 0) {
        state.mediaflow.flows.splice(flowIndex, 1);
      }
    }
    const mf = state.mediaflows.find(mf => mf._id === mediaflowId);
    if (mf && mf.flows) {
      const flowIndex = mf.flows.findIndex(flow => flow._id === flowId);
      if (flowIndex >= 0) {
        mf.flows.splice(flowIndex, 1);
      }
    }
  },
  SET_FLOW_NAME(state, { mediaflowId, flowId, name }) {
    if (state.mediaflow._id === mediaflowId) {
      const flow = state.mediaflow.flows?.find(f => f._id === flowId);
      if (flow) {
        flow.name = name;
      }
    }
    const mf = state.mediaflows.find(mf => mf._id === mediaflowId);
    if (mf && mf.flows) {
      const flow = mf.flows.find(f => f._id === flowId);
      if (flow) {
        flow.name = name;
      }
    }
  },
  SWITCH_FLOW(state, { mediaflowId, flowId }) {
    // Update current mediaflow
    if (state.mediaflow._id === mediaflowId && state.mediaflow.flows) {
      state.mediaflow.flows.forEach(flow => {
        flow.active = flow._id === flowId;
      });
    }
    // Update mediaflows array
    const mf = state.mediaflows.find(mf => mf._id === mediaflowId);
    if (mf && mf.flows) {
      mf.flows.forEach(flow => {
        flow.active = flow._id === flowId;
      });
    }
  },
  CLEAR_OBS_INSTANCE(state, mediaflowId) {
    if (state.mediaflow._id === mediaflowId) {
      state.mediaflow.obsInstance = null;
      state.mediaflow.flowInstanceReservedAt = null;
      state.mediaflow.flowInstanceReserveTimeSec = null;
    }
    
    const mf = state.mediaflows.find(mf => mf._id === mediaflowId);
    if (mf) {
      mf.obsInstance = null;
      mf.flowInstanceReservedAt = null;
      mf.flowInstanceReserveTimeSec = null;
    }
  },
  SET_FLOWS(state, { mediaflowId, flows }) {
    if (state.mediaflow._id === mediaflowId) state.mediaflow.flows = flows;
  },
  SET_FLOW_FIELDS(state, { mediaflowId, flowId, fields }) {
    [state.mediaflow, ...state.mediaflows].forEach((mf) => {
      if (!mf || mf._id !== mediaflowId) return;
      const flow = mf.flows?.find((f) => f._id === flowId);
      if (flow) Object.assign(flow, fields);
    });
  },
  // ---------------------------------------------------------------- overlays
  SET_OVERLAYS(state, { mediaflowId, overlays }) {
    const list = (Array.isArray(overlays) ? overlays : []).map(normalizeOverlay).sort((a, b) => a.order - b.order);
    if (state.mediaflow._id === mediaflowId) state.mediaflow.overlays = list;
    const mf = state.mediaflows.find((m) => m._id === mediaflowId);
    if (mf) mf.overlays = _.cloneDeep(list);
  },
  ADD_OVERLAY(state, { mediaflowId, overlay }) {
    if (state.mediaflow._id !== mediaflowId) return;
    if (!Array.isArray(state.mediaflow.overlays)) state.mediaflow.overlays = [];
    state.mediaflow.overlays.push(normalizeOverlay(overlay, state.mediaflow.overlays.length));
  },
  UPDATE_OVERLAY(state, { mediaflowId, overlayId, overlay }) {
    if (state.mediaflow._id !== mediaflowId) return;
    const current = state.mediaflow.overlays?.find((o) => o._id === overlayId);
    if (!current) return;
    const { media, ...rest } = overlay || {};
    Object.assign(current, rest);
    if (media && typeof media === "object") {
      current.media = { ...(current.media || {}), ...media, show: true };
      if (!current.media.id) current.media.id = current.media._id || `overlay-${current._id}`;
    }
  },
  REMOVE_OVERLAY(state, { mediaflowId, overlayId }) {
    if (state.mediaflow._id !== mediaflowId) return;
    const idx = state.mediaflow.overlays?.findIndex((o) => o._id === overlayId) ?? -1;
    if (idx >= 0) state.mediaflow.overlays.splice(idx, 1);
  },
  // ---------------------------------------------------------------- break cue
  SET_CUE(state, { mediaflowId, cue }) {
    if (state.mediaflow._id === mediaflowId) state.mediaflow.cue = cue || null;
  },
  SET_OBS_INSTANCE(state, { mediaflowId, obsInstance, flowInstanceReservedAt, flowInstanceReserveTimeSec }) {
    if (state.mediaflow._id === mediaflowId) {
      state.mediaflow.obsInstance = obsInstance;
      state.mediaflow.flowInstanceReservedAt = flowInstanceReservedAt;
      state.mediaflow.flowInstanceReserveTimeSec = flowInstanceReserveTimeSec;
    }
    const mf = state.mediaflows.find(mf => mf._id === mediaflowId);
    if (mf) {
      mf.obsInstance = obsInstance;
      mf.flowInstanceReservedAt = flowInstanceReservedAt;
      mf.flowInstanceReserveTimeSec = flowInstanceReserveTimeSec;
    }
  },
};

const actions = {
  async getMediaflows({ commit, state }, forceRefresh = false) {
    if (state.isLoaded && !forceRefresh) {
      return state.mediaflows;
    }

    if (state.isLoading) {
      return new Promise((resolve) => {
        const checkLoading = setInterval(() => {
          if (!state.isLoading) {
            clearInterval(checkLoading);
            resolve(state.mediaflows);
          }
        }, 50);
      });
    }

    commit("SET_LOADING", true);
    try {
      const mediaflows = await MediaflowService.getMediaflows();
      commit("GET_MEDIAFLOWS", mediaflows);
      return mediaflows;
    } catch (error) {
      commit("SET_LOADING", false);
      throw error;
    }
  },
  seedMediaflow({ commit, state }, id) {
    const listed = state.mediaflows.find((mediaflow) => mediaflow._id === id);
    if (!listed) return false;
    commit("GET_MEDIAFLOW", normalizeMediaflow(listed));
    return true;
  },
  async fetchMediaflow(_context, id) {
    return normalizeMediaflow(await MediaflowService.getMediaflow(id));
  },
  applyMediaflow({ commit, state }, mediaflow) {
    const next = normalizeMediaflow(mediaflow);
    // A running break cue is known from the POST that started it. Servers that
    // do not echo `cue` on GET must not wipe it mid-break; servers that do win.
    const local = state.mediaflow?.cue;
    if (next.cue === undefined && local && state.mediaflow?._id === next._id) {
      const stillRunning = !local.returnsAt || new Date(local.returnsAt).getTime() > Date.now();
      next.cue = stillRunning ? local : null;
    }
    commit("GET_MEDIAFLOW", next);
  },
  async getMediaflow({ dispatch }, id) {
    const mediaflow = await dispatch("fetchMediaflow", id);
    dispatch("applyMediaflow", mediaflow);
    return mediaflow;
  },
  setSource({ commit }, { index, stream }) {
    commit("SET_MEDIAFLOW_SOURCE_STREAM", { index, stream })
  },
  setCurrentMediaflow({ commit }, id) {
    commit("SET_CURRENT_MEDIAFLOW", id);
  },
  removeMediaflow({ commit }, { id }) {
    commit("REMOVE_MEDIAFLOW", id)
  },
  toggleMediaflow({ commit }, { id }) {
    commit("TOGGLE_MEDIAFLOW", id)
  },
  setLayout({ commit }, { id, streams, layout }) {
    commit("SET_MEDIAFLOW_LAYOUT", { id, streams, layout })
  },
  setName({ commit }, { id, name }) {
    commit("SET_MEDIAFLOW_NAME", { id, name })
  },
  setDestination({ commit }, { id, destination }) {
    commit("SET_MEDIAFLOW_DESTINATION", { id, destination })
  },
  setResolutionPreset({ commit }, { id, resolutionPreset }) {
    commit("SET_MEDIAFLOW_RESOLUTION_PRESET", { id, resolutionPreset })
  },
  setScreenResolution({ commit }, { id, screenResolution }) {
    commit("SET_MEDIAFLOW_SCREEN_RESOLUTION", { id, screenResolution })
  },
  setBitrate({ commit }, { id, bitrate }) {
    commit("SET_MEDIAFLOW_BITRATE", { id, bitrate })
  },
  setFps({ commit }, { id, fps }) {
    commit("SET_MEDIAFLOW_FPS", { id, fps })
  },
  setTeamPermissionError({ commit }, { error }) {
    commit("SET_MEDIAFLOW_TEAM_PERMISSION_ERROR", { perm: error })
  },
  addFlow({ commit }, { mediaflowId, flow }) {
    commit("ADD_FLOW", { mediaflowId, flow })
  },
  addFlowMedia({ commit }, { mediaflowId, flowId, media }) {
    commit("ADD_FLOW_MEDIA", { mediaflowId, flowId, media })
  },
  updateFlowMedia({ commit }, { mediaflowId, flowId, mediaId, media }) {
    commit("UPDATE_FLOW_MEDIA", { mediaflowId, flowId, mediaId, media })
  },
  updateFlowMediaVolume({ commit }, { mediaflowId, flowId, mediaId, volume }) {
    commit("UPDATE_FLOW_MEDIA_VOLUME", { mediaflowId, flowId, mediaId, volume })
  },
  updateFlowMediaMuted({ commit }, { mediaflowId, flowId, mediaId, muted }) {
    commit("UPDATE_FLOW_MEDIA_MUTED", { mediaflowId, flowId, mediaId, muted })
  },
  updateFlowMediaAudioOffset({ commit }, { mediaflowId, flowId, mediaId, audioOffset }) {
    commit("UPDATE_FLOW_MEDIA_AUDIO_OFFSET", { mediaflowId, flowId, mediaId, audioOffset })
  },
  reorderFlowMedia({ commit }, { mediaflowId, flowId, fromIndex, toIndex }) {
    commit("REORDER_FLOW_MEDIA", { mediaflowId, flowId, fromIndex, toIndex })
  },
  clearFlowMedia({ commit }, { mediaflowId, flowId }) {
    commit("CLEAR_FLOW_MEDIA", { mediaflowId, flowId })
  },
  async toggleFlowMediaShow({ commit }, { mediaflowId, flowId, mediaId }) {
    await MediaflowService.toggleFlowMedia(mediaflowId, flowId, mediaId);
    commit('TOGGLE_FLOW_MEDIA_SHOW', { mediaflowId, flowId, mediaId });
  },
  async deleteFlowMedia({ commit }, { mediaflowId, flowId, mediaId }) {
    const res = await MediaflowService.deleteFlowMedia(mediaflowId, flowId, mediaId);
    if (res && res.success !== false) {
      commit('DELETE_FLOW_MEDIA', { mediaflowId, flowId, mediaId });
    }
    return res;
  },
  async removeFlow({ commit }, { mediaflowId, flowId }) {
    const res = await MediaflowService.deleteFlow(mediaflowId, flowId);
    if (res && res.success !== false) {
      commit('REMOVE_FLOW', { mediaflowId, flowId });
    }
    return res;
  },
  setFlowName({ commit }, { mediaflowId, flowId, name }) {
    commit('SET_FLOW_NAME', { mediaflowId, flowId, name });
  },
  async switchFlow({ commit }, { mediaflowId, flowId }) {
    const res = await MediaflowService.switchScene(mediaflowId, flowId);
    if (res && res.success !== false) {
      commit('SWITCH_FLOW', { mediaflowId, flowId });
    }
    return res;
  },
  setFlowFields({ commit }, { mediaflowId, flowId, fields }) {
    commit('SET_FLOW_FIELDS', { mediaflowId, flowId, fields });
  },
  // ---------------------------------------------------------------- overlays
  addOverlay({ commit }, { mediaflowId, overlay }) {
    commit('ADD_OVERLAY', { mediaflowId, overlay });
  },
  /** Local-only patch (optimistic UI); no request. */
  patchOverlay({ commit }, { mediaflowId, overlayId, overlay }) {
    commit('UPDATE_OVERLAY', { mediaflowId, overlayId, overlay });
  },
  setFlows({ commit }, { mediaflowId, flows }) {
    commit('SET_FLOWS', { mediaflowId, flows });
  },
  async createOverlay({ commit }, { mediaflowId, data }) {
    const overlay = await MediaflowService.createOverlay(mediaflowId, data);
    if (overlay && overlay._id) commit('ADD_OVERLAY', { mediaflowId, overlay });
    return overlay;
  },
  async updateOverlay({ commit }, { mediaflowId, overlayId, data }) {
    // Optimistic: the canvas and the tile reflect the edit at once.
    commit('UPDATE_OVERLAY', { mediaflowId, overlayId, overlay: data });
    const overlay = await MediaflowService.updateOverlay(mediaflowId, overlayId, data);
    if (overlay && overlay._id) commit('UPDATE_OVERLAY', { mediaflowId, overlayId, overlay });
    return overlay;
  },
  async deleteOverlay({ commit }, { mediaflowId, overlayId }) {
    const res = await MediaflowService.deleteOverlay(mediaflowId, overlayId);
    if (res && res.success !== false) commit('REMOVE_OVERLAY', { mediaflowId, overlayId });
    return res;
  },
  async reorderOverlays({ commit, state }, { mediaflowId, ids }) {
    const current = state.mediaflow._id === mediaflowId ? (state.mediaflow.overlays || []) : [];
    const byId = new Map(current.map((o) => [o._id, o]));
    const reordered = ids.map((id, i) => ({ ...byId.get(id), order: i })).filter((o) => o._id);
    commit('SET_OVERLAYS', { mediaflowId, overlays: reordered });
    return MediaflowService.reorderOverlays(mediaflowId, ids);
  },
  async takeOverlay({ commit }, { mediaflowId, overlayId, on, transition }) {
    const res = await MediaflowService.takeOverlay(mediaflowId, overlayId, { on, transition });
    if (res && res.success !== false) {
      commit('UPDATE_OVERLAY', { mediaflowId, overlayId, overlay: { live: typeof res.live === 'boolean' ? res.live : !!on } });
    }
    return res;
  },
  // ---------------------------------------------------------------- break cue
  async cueFlow({ commit }, { mediaflowId, flowId, data }) {
    const res = await MediaflowService.cueFlow(mediaflowId, flowId, data);
    if (res && res.success !== false) {
      commit('SWITCH_FLOW', { mediaflowId, flowId });
      commit('SET_CUE', {
        mediaflowId,
        cue: {
          cueId: res.cueId || null,
          flowId,
          returnsAt: res.returnsAt || null,
          returnFlowId: res.returnFlowId ?? data?.returnFlowId ?? null,
          startedAt: Date.now(),
        },
      });
    }
    return res;
  },
  async endCue({ commit, state }, { mediaflowId }) {
    const res = await MediaflowService.endCue(mediaflowId);
    if (res && res.success !== false) {
      const returnTo = res.flowId || res.returnFlowId || state.mediaflow?.cue?.returnFlowId;
      if (returnTo) commit('SWITCH_FLOW', { mediaflowId, flowId: returnTo });
      commit('SET_CUE', { mediaflowId, cue: null });
    }
    return res;
  },
  setCue({ commit }, { mediaflowId, cue }) {
    commit('SET_CUE', { mediaflowId, cue });
  },
  clearObsInstance({ commit }, mediaflowId) {
    commit('CLEAR_OBS_INSTANCE', mediaflowId);
  },
  setObsInstance({ commit }, { mediaflowId, obsInstance, flowInstanceReservedAt, flowInstanceReserveTimeSec }) {
    commit('SET_OBS_INSTANCE', { mediaflowId, obsInstance, flowInstanceReservedAt, flowInstanceReserveTimeSec });
  },
};

const getters = {
  mediaflows: (state) => state.mediaflows,
  mediaflow: (state) => state.mediaflow,
  currentMediaflow: (state) => {
    if (state.currentMediaflowId === '') {
      return state.mediaflows.find(
        (mf) =>
          mf._id === (router.currentRoute.params && router.currentRoute.params.id)
      );
    } else {
      return state.mediaflows.find((mf) => mf._id === state.currentMediaflowId);
    }
  },
  mediaflowLoaded: (state) => state.mediaflowLoaded,
  teamPermissionError: (state) => state.teamPermissionError,
  isLoaded: (state) => state.isLoaded,
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
}; 